import {
  CLIENT_PROTOCOL_HEADER,
  CLIENT_PROTOCOL_VERSION,
  clientArtifactsSchema,
  clientDocumentSchema,
  clientHealthSchema,
  clientProtocolEventSchema,
  clientRedactionDetectionSchema,
  clientRunListSchema,
  clientRunSchema,
  type ClientArtifacts,
  type ClientDocument,
  type ClientProtocolEvent,
  type ClientRedactionDetection,
  type ClientRun,
} from '@okrapdf/schemas/client-protocol';
import { basename } from 'node:path';
import { Clock, Context, Data, Effect, Layer, Schema } from 'effect';
import {
  ClientHttpError,
  readClientResponseTextEffect,
  withFetchedResponseEffect,
} from '../client';

interface ResponseSchema<Output> {
  readonly safeParse: (
    input: unknown,
  ) => { readonly success: true; readonly data: Output } | { readonly success: false };
}

export interface WorkbenchEndpoint {
  readonly baseUrl: string;
  readonly token?: string;
}

export interface WorkbenchParseInput {
  readonly endpoint: WorkbenchEndpoint;
  readonly filename: string;
  readonly pdf?: Uint8Array;
  readonly sourcePath?: string;
  readonly parserId: string;
  readonly providerId?: string;
  readonly model?: string;
  readonly wait: boolean;
  readonly pollIntervalMs?: number;
  readonly waitTimeoutMs?: number;
}

export interface WorkbenchParseResult {
  readonly document: ClientDocument;
  readonly run: ClientRun;
}

export interface WorkbenchRunInput {
  readonly endpoint: WorkbenchEndpoint;
  readonly runId: string;
}

export interface WorkbenchWaitRunInput extends WorkbenchRunInput {
  readonly pollIntervalMs?: number;
  readonly waitTimeoutMs?: number;
}

export class WorkbenchClientError extends Schema.TaggedError<WorkbenchClientError>()(
  'WorkbenchClientError',
  {
    operation: Schema.String,
    code: Schema.String,
    message: Schema.String,
    retryable: Schema.Boolean,
    status: Schema.optionalKey(Schema.Number),
  },
) {}

export class WorkbenchOperationError extends Data.TaggedError('WorkbenchOperationError')<{
  readonly operation: string;
  readonly code: string;
  readonly retryable: boolean;
  readonly status?: number;
}> {}

const workbenchCauses = new WeakMap<WorkbenchOperationError, unknown>();

export function workbenchErrorCause(error: WorkbenchOperationError): unknown {
  return workbenchCauses.get(error) ?? error;
}

interface WorkbenchTransportService {
  readonly execute: (
    request: Request,
    operation: string,
  ) => Effect.Effect<
    {
      readonly response: Response;
      readonly raw: string;
    },
    WorkbenchOperationError
  >;
}

class WorkbenchTransport extends Context.Service<WorkbenchTransport, WorkbenchTransportService>()(
  'okrapdf/cli/WorkbenchTransport',
) {}

export type WorkbenchProgram<A, E = WorkbenchOperationError> = Effect.Effect<
  A,
  E,
  WorkbenchTransport
>;

function clientError(
  options: {
    readonly operation: string;
    readonly code: string;
    readonly message: string;
    readonly retryable: boolean;
    readonly status?: number;
  },
  cause?: unknown,
): WorkbenchOperationError {
  const { message: _message, ...safe } = options;
  const error = new WorkbenchOperationError(safe);
  workbenchCauses.set(error, cause ?? new WorkbenchClientError(options));
  return error;
}

function endpointUrl(endpoint: WorkbenchEndpoint, path: string): URL {
  let base: URL;
  try {
    base = new URL(endpoint.baseUrl);
  } catch {
    throw clientError({
      operation: 'resolve_endpoint',
      code: 'invalid_endpoint',
      message: `Invalid workbench server URL: ${endpoint.baseUrl}`,
      retryable: false,
    });
  }
  if (!['http:', 'https:'].includes(base.protocol) || base.username || base.password) {
    throw clientError({
      operation: 'resolve_endpoint',
      code: 'invalid_endpoint',
      message: 'Workbench server URLs must use HTTP(S) and may not contain credentials.',
      retryable: false,
    });
  }
  const [pathname, search = ''] = path.split('?', 2);
  base.pathname = `${base.pathname.replace(/\/$/, '')}${pathname}`;
  base.search = search;
  base.hash = '';
  return base;
}

function requestHeaders(endpoint: WorkbenchEndpoint): Headers {
  const headers = new Headers({
    accept: 'application/json',
    [CLIENT_PROTOCOL_HEADER]: CLIENT_PROTOCOL_VERSION,
  });
  if (endpoint.token) headers.set('authorization', `Bearer ${endpoint.token}`);
  return headers;
}

function responseErrorMessage(payload: unknown, status: number): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const error = (payload as { error: unknown }).error;
    if (typeof error === 'string' && error.trim()) return error;
    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
  }
  return `Workbench server returned HTTP ${status}.`;
}

const decodeResponse = Effect.fn('okrapdf/cli.WorkbenchClient.decodeResponse')(function* <Output>(
  operation: string,
  response: Response,
  schema: ResponseSchema<Output>,
  raw: string,
): Effect.fn.Return<Output, WorkbenchOperationError> {
  const payload = yield* Effect.try({
    try: () => JSON.parse(raw) as unknown,
    catch: () =>
      clientError({
        operation,
        code: 'invalid_response',
        message: `Workbench server returned a non-JSON response for ${operation}.`,
        retryable: false,
        status: response.status,
      }),
  });
  if (!response.ok) {
    return yield* clientError({
      operation,
      code: 'request_failed',
      message: responseErrorMessage(payload, response.status),
      retryable: response.status >= 500,
      status: response.status,
    });
  }
  const decoded = schema.safeParse(payload);
  if (!decoded.success) {
    return yield* clientError({
      operation,
      code: operation === 'health' ? 'protocol_mismatch' : 'invalid_response',
      message:
        operation === 'health'
          ? `Server does not speak ${CLIENT_PROTOCOL_VERSION}.`
          : `Workbench server returned an invalid ${operation} response.`,
      retryable: false,
      status: response.status,
    });
  }
  return decoded.data;
});

function workbenchRequestEffect(endpoint: WorkbenchEndpoint, path: string, init?: RequestInit) {
  return Effect.try({
    try: () => {
      const headers = requestHeaders(endpoint);
      for (const [key, value] of new Headers(init?.headers)) headers.set(key, value);
      return new Request(endpointUrl(endpoint, path), { ...init, headers });
    },
    catch: (cause) =>
      cause instanceof WorkbenchOperationError
        ? cause
        : clientError(
            {
              operation: 'request',
              code: 'invalid_request',
              message: 'Invalid workbench request.',
              retryable: false,
            },
            cause,
          ),
  });
}

const executeJson = Effect.fn('okrapdf/cli.WorkbenchClient.executeJson')(function* <Output>(
  endpoint: WorkbenchEndpoint,
  operation: string,
  path: string,
  schema: ResponseSchema<Output>,
  init?: RequestInit,
): Effect.fn.Return<Output, WorkbenchOperationError, WorkbenchTransport> {
  const transport = yield* WorkbenchTransport;
  const request = yield* workbenchRequestEffect(endpoint, path, init);
  const { response, raw } = yield* transport.execute(request, operation);
  return yield* decodeResponse(operation, response, schema, raw);
});

function terminal(status: ClientRun['status']): boolean {
  return !['queued', 'running', 'cancelling'].includes(status);
}

const verifyWorkbenchProtocolEffect = Effect.fn('okrapdf/cli.WorkbenchClient.verifyProtocol')(
  function* (endpoint: WorkbenchEndpoint) {
    return yield* executeJson(endpoint, 'health', '/global/health', clientHealthSchema);
  },
);

const readWorkbenchRunEffect = Effect.fn('okrapdf/cli.WorkbenchClient.readRun')(function* (
  input: WorkbenchRunInput,
) {
  return yield* executeJson(
    input.endpoint,
    'run read',
    `/run/${encodeURIComponent(input.runId)}`,
    clientRunSchema,
  );
});

const waitForWorkbenchRunSnapshotEffect = Effect.fn(
  'okrapdf/cli.WorkbenchClient.waitForRunSnapshot',
)((input: WorkbenchWaitRunInput, initial?: ClientRun) =>
  Effect.suspend(() => {
    if (initial && terminal(initial.status)) return Effect.succeed(initial);
    let run = initial;
    const timeoutMs =
      input.waitTimeoutMs !== undefined && Number.isFinite(input.waitTimeoutMs)
        ? Math.max(0, input.waitTimeoutMs)
        : undefined;
    const timeout = () =>
      clientError({
        operation: 'run wait',
        code: 'wait_timeout',
        message: 'Timed out waiting for run ' + (run?.id ?? input.runId) + '.',
        retryable: true,
      });
    const program = Effect.gen(function* () {
      const startedAt = yield* Clock.currentTimeMillis;
      const checkDeadline = Effect.gen(function* () {
        if (timeoutMs !== undefined && (yield* Clock.currentTimeMillis) - startedAt >= timeoutMs) {
          return yield* timeout();
        }
      });
      // A standalone wait owns protocol verification and its first run read too.
      // Parse supplies its accepted run; its wait deadline starts after acceptance.
      if (!run) {
        yield* verifyWorkbenchProtocolEffect(input.endpoint);
        yield* checkDeadline;
        run = yield* readWorkbenchRunEffect(input);
      }
      while (!terminal(run.status)) {
        yield* checkDeadline;
        yield* Effect.sleep(input.pollIntervalMs ?? 500);
        yield* checkDeadline;
        run = yield* readWorkbenchRunEffect(input);
      }
      return run;
    });
    return timeoutMs === undefined
      ? program
      : program.pipe(
          Effect.timeoutOrElse({
            duration: timeoutMs,
            orElse: () => Effect.fail(timeout()),
          }),
        );
  }),
);

export const runWorkbenchParseEffect = Effect.fn('okrapdf/cli.runWorkbenchParse')(function* (
  input: WorkbenchParseInput,
): Effect.fn.Return<WorkbenchParseResult, WorkbenchOperationError, WorkbenchTransport> {
  yield* verifyWorkbenchProtocolEffect(input.endpoint);

  if ((input.pdf === undefined) === (input.sourcePath === undefined)) {
    return yield* clientError({
      operation: 'document registration',
      code: 'invalid_source',
      message: 'Provide exactly one PDF byte source or local source path.',
      retryable: false,
    });
  }
  const registration = input.sourcePath
    ? {
        headers: new Headers({ 'content-type': 'application/json' }),
        body: JSON.stringify({ source: input.sourcePath }),
      }
    : {
        headers: new Headers({
          'content-type': 'application/pdf',
          'x-file-name': basename(input.filename),
        }),
        body: Buffer.from(input.pdf!),
      };
  const document = yield* executeJson(
    input.endpoint,
    'document registration',
    '/document',
    clientDocumentSchema,
    {
      method: 'POST',
      ...registration,
    },
  );
  let run = yield* executeJson(
    input.endpoint,
    'parse start',
    `/document/${encodeURIComponent(document.id)}/parse`,
    clientRunSchema,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        parserId: input.parserId,
        ...(input.providerId ? { providerId: input.providerId } : {}),
        ...(input.model ? { model: input.model } : {}),
      }),
    },
  );
  if (!input.wait) return { document, run };
  run = yield* waitForWorkbenchRunSnapshotEffect(
    {
      endpoint: input.endpoint,
      runId: run.id,
      ...(input.pollIntervalMs === undefined ? {} : { pollIntervalMs: input.pollIntervalMs }),
      ...(input.waitTimeoutMs === undefined ? {} : { waitTimeoutMs: input.waitTimeoutMs }),
    },
    run,
  );
  return { document, run };
});

export const listWorkbenchRunsEffect = Effect.fn('okrapdf/cli.listWorkbenchRuns')(function* (
  endpoint: WorkbenchEndpoint,
) {
  yield* verifyWorkbenchProtocolEffect(endpoint);
  const result = yield* executeJson(endpoint, 'run list', '/run', clientRunListSchema);
  return result.data;
});

export const getWorkbenchRunEffect = Effect.fn('okrapdf/cli.getWorkbenchRun')(function* (
  input: WorkbenchRunInput,
) {
  yield* verifyWorkbenchProtocolEffect(input.endpoint);
  return yield* readWorkbenchRunEffect(input);
});

export const waitForWorkbenchRunEffect = Effect.fn('okrapdf/cli.waitForWorkbenchRun')(function* (
  input: WorkbenchWaitRunInput,
) {
  return yield* waitForWorkbenchRunSnapshotEffect(input);
});

export const getWorkbenchRunArtifactsEffect = Effect.fn('okrapdf/cli.getWorkbenchRunArtifacts')(
  function* (input: WorkbenchRunInput) {
    yield* verifyWorkbenchProtocolEffect(input.endpoint);
    return yield* executeJson(
      input.endpoint,
      'run artifacts',
      `/run/${encodeURIComponent(input.runId)}/artifacts`,
      clientArtifactsSchema,
    );
  },
);

function parseEventReplay(raw: string, status: number): ClientProtocolEvent[] {
  const events: ClientProtocolEvent[] = [];
  for (const chunk of raw.split(/\r?\n\r?\n/)) {
    const data = chunk
      .split(/\r?\n/)
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice('data:'.length).trimStart())
      .join('\n');
    if (!data) continue;
    let payload: unknown;
    try {
      payload = JSON.parse(data);
    } catch {
      throw clientError({
        operation: 'run events',
        code: 'invalid_response',
        message: 'Workbench server returned malformed SSE event JSON.',
        retryable: false,
        status,
      });
    }
    const decoded = clientProtocolEventSchema.safeParse(payload);
    if (!decoded.success) {
      throw clientError({
        operation: 'run events',
        code: 'invalid_response',
        message: 'Workbench server returned an invalid client-protocol event.',
        retryable: false,
        status,
      });
    }
    events.push(decoded.data);
  }
  return events;
}

export const getWorkbenchRunEventsEffect = Effect.fn('okrapdf/cli.getWorkbenchRunEvents')(
  function* (input: WorkbenchRunInput) {
    yield* verifyWorkbenchProtocolEffect(input.endpoint);
    const transport = yield* WorkbenchTransport;
    const request = yield* workbenchRequestEffect(
      input.endpoint,
      '/run/' + encodeURIComponent(input.runId) + '/events?once=1',
      { headers: { accept: 'text/event-stream' } },
    );
    const { response, raw } = yield* transport.execute(request, 'run events');
    if (!response.ok) {
      let payload: unknown = raw;
      try {
        payload = JSON.parse(raw);
      } catch {
        // A plain-text response still gets the HTTP status fallback below.
      }
      return yield* clientError({
        operation: 'run events',
        code: 'request_failed',
        message: responseErrorMessage(payload, response.status),
        retryable: response.status >= 500,
        status: response.status,
      });
    }
    if (!response.headers.get('content-type')?.includes('text/event-stream')) {
      return yield* clientError({
        operation: 'run events',
        code: 'invalid_response',
        message: 'Workbench server returned a non-SSE run event response.',
        retryable: false,
        status: response.status,
      });
    }
    return yield* Effect.try({
      try: () => parseEventReplay(raw, response.status),
      catch: (error) =>
        error instanceof WorkbenchOperationError
          ? error
          : clientError({
              operation: 'run events',
              code: 'invalid_response',
              message: 'Workbench server event replay could not be decoded.',
              retryable: false,
              status: response.status,
            }),
    });
  },
);

export const analyzeWorkbenchPiiEffect = Effect.fn('okrapdf/cli.analyzeWorkbenchPii')(function* (
  input: WorkbenchRunInput,
) {
  yield* verifyWorkbenchProtocolEffect(input.endpoint);
  return yield* executeJson(
    input.endpoint,
    'PII analysis',
    `/run/${encodeURIComponent(input.runId)}/pii/analyze`,
    clientRedactionDetectionSchema,
    { method: 'POST' },
  );
});

export const getWorkbenchPiiAnalysisEffect = Effect.fn('okrapdf/cli.getWorkbenchPiiAnalysis')(
  function* (input: WorkbenchRunInput) {
    yield* verifyWorkbenchProtocolEffect(input.endpoint);
    return yield* executeJson(
      input.endpoint,
      'PII analysis read',
      `/run/${encodeURIComponent(input.runId)}/pii/analysis`,
      clientRedactionDetectionSchema,
    );
  },
);

function transportLayer(
  fetchImplementation: typeof globalThis.fetch,
): Layer.Layer<WorkbenchTransport> {
  return Layer.succeed(
    WorkbenchTransport,
    WorkbenchTransport.of({
      execute: Effect.fn('okrapdf/cli.WorkbenchTransport.execute')(function* (
        request: Request,
        operation: string,
      ) {
        yield* Effect.yieldNow;
        return yield* withFetchedResponseEffect(
          { signal: request.signal },
          (init) => fetchImplementation(new Request(request, { signal: init.signal })),
          (response) =>
            readClientResponseTextEffect(response).pipe(
              Effect.map((raw) => ({ response, raw })),
              Effect.mapError(
                (error) =>
                  new ClientHttpError({
                    method: 'workbench_response',
                    status: response.status,
                    cause: error.cause,
                  }),
              ),
            ),
        ).pipe(
          Effect.mapError((error) =>
            error.method === 'workbench_response'
              ? clientError({
                  operation,
                  code: 'invalid_response',
                  message:
                    operation === 'run events'
                      ? 'Workbench server event replay could not be read.'
                      : 'Workbench server returned a non-JSON response for ' + operation + '.',
                  retryable: operation === 'run events',
                  status: error.status,
                })
              : clientError({
                  operation: 'request',
                  code: 'connection_failed',
                  message:
                    'Could not reach workbench server at ' + new URL(request.url).origin + '.',
                  retryable: true,
                }),
          ),
        );
      }),
    }),
  );
}

/** Compose all workbench stages in the caller's native runtime. */
export function withWorkbenchTransport<A, E>(
  program: Effect.Effect<A, E, WorkbenchTransport>,
  fetchImplementation: typeof globalThis.fetch = globalThis.fetch,
): Effect.Effect<A, E> {
  return Effect.andThen(
    Effect.yieldNow,
    Effect.provide(program, transportLayer(fetchImplementation)),
  );
}

function runWorkbenchEffect<A>(
  program: Effect.Effect<A, WorkbenchOperationError, WorkbenchTransport>,
  fetchImplementation: typeof globalThis.fetch,
  signal?: AbortSignal,
): Promise<A> {
  return Effect.runPromise(
    withWorkbenchTransport(program, fetchImplementation).pipe(Effect.mapError(workbenchErrorCause)),
    { signal },
  );
}

/** Promise bridge for the Commander CLI adapter. */
export function runWorkbenchParse(
  input: WorkbenchParseInput,
  fetchImplementation: typeof globalThis.fetch = globalThis.fetch,
  signal?: AbortSignal,
): Promise<WorkbenchParseResult> {
  return runWorkbenchEffect(runWorkbenchParseEffect(input), fetchImplementation, signal);
}

export function listWorkbenchRuns(
  endpoint: WorkbenchEndpoint,
  fetchImplementation: typeof globalThis.fetch = globalThis.fetch,
  signal?: AbortSignal,
): Promise<ClientRun[]> {
  return runWorkbenchEffect(listWorkbenchRunsEffect(endpoint), fetchImplementation, signal);
}

export function getWorkbenchRun(
  input: WorkbenchRunInput,
  fetchImplementation: typeof globalThis.fetch = globalThis.fetch,
  signal?: AbortSignal,
): Promise<ClientRun> {
  return runWorkbenchEffect(getWorkbenchRunEffect(input), fetchImplementation, signal);
}

export function waitForWorkbenchRun(
  input: WorkbenchWaitRunInput,
  fetchImplementation: typeof globalThis.fetch = globalThis.fetch,
  signal?: AbortSignal,
): Promise<ClientRun> {
  return runWorkbenchEffect(waitForWorkbenchRunEffect(input), fetchImplementation, signal);
}

export function getWorkbenchRunArtifacts(
  input: WorkbenchRunInput,
  fetchImplementation: typeof globalThis.fetch = globalThis.fetch,
  signal?: AbortSignal,
): Promise<ClientArtifacts> {
  return runWorkbenchEffect(getWorkbenchRunArtifactsEffect(input), fetchImplementation, signal);
}

export function getWorkbenchRunEvents(
  input: WorkbenchRunInput,
  fetchImplementation: typeof globalThis.fetch = globalThis.fetch,
  signal?: AbortSignal,
): Promise<ClientProtocolEvent[]> {
  return runWorkbenchEffect(getWorkbenchRunEventsEffect(input), fetchImplementation, signal);
}

export function analyzeWorkbenchPii(
  input: WorkbenchRunInput,
  fetchImplementation: typeof globalThis.fetch = globalThis.fetch,
  signal?: AbortSignal,
): Promise<ClientRedactionDetection> {
  return runWorkbenchEffect(analyzeWorkbenchPiiEffect(input), fetchImplementation, signal);
}

export function getWorkbenchPiiAnalysis(
  input: WorkbenchRunInput,
  fetchImplementation: typeof globalThis.fetch = globalThis.fetch,
  signal?: AbortSignal,
): Promise<ClientRedactionDetection> {
  return runWorkbenchEffect(getWorkbenchPiiAnalysisEffect(input), fetchImplementation, signal);
}
